import React, { useState } from 'react';
import classNames from 'classnames/bind';
import {
    CodeOutlined,
    DatabaseOutlined,
    CloudOutlined,
    AppstoreOutlined,
    BulbOutlined,
    SolutionOutlined,
    CheckOutlined,
    TrophyOutlined,
    ExperimentOutlined,
    BookOutlined,
    FileDoneOutlined,
    YoutubeOutlined,
    GithubOutlined,
    FullscreenOutlined,
} from '@ant-design/icons';
import { Image, Row, Col } from 'antd';
import styles from './ProjectDetail.module.scss';

const cx = classNames.bind(styles);

function ProjectDetail({ detail, isVisible }) {
    const [activeTab, setActiveTab] = useState('description');

    const {
        description,
        problem,
        solution,
        features,
        technologies,
        architecture,
        screenshots,
        results,
        challenges,
        learnings,
        conclusion,
        responsibilities,
        githubLink,
        previewLink,
    } = detail;

    const tabs = [
        { id: 'description', label: 'Tổng quan', icon: <AppstoreOutlined /> },
        { id: 'problem', label: 'Vấn đề', icon: <BulbOutlined /> },
        { id: 'solution', label: 'Giải pháp', icon: <SolutionOutlined /> },
        { id: 'features', label: 'Tính năng', icon: <CheckOutlined /> },
        { id: 'technologies', label: 'Công nghệ', icon: <CodeOutlined /> },
        { id: 'results', label: 'Kết quả', icon: <TrophyOutlined /> },
        { id: 'challenges', label: 'Thách thức', icon: <ExperimentOutlined /> },
        { id: 'learnings', label: 'Bài học', icon: <BookOutlined /> },
        { id: 'conclusion', label: 'Kết luận', icon: <FileDoneOutlined /> },
    ].filter((tab) => detail[tab.id]); // Chỉ hiển thị tab nếu có dữ liệu

    const renderTabContent = (tabId) => {
        // Hàm này render nội dung của tab dựa trên tabId
        const tabContent = detail[tabId];

        // Nếu tabContent là một hàm, gọi nó để render JSX
        if (typeof tabContent === 'function') {
            return tabContent();
        }

        // Nếu không, trả về nội dung dạng chuỗi (cho các tab không có JSX)
        return (
            <div className={cx('content-section')}>
                {tabId === 'features' && features && features.length > 0 && (
                    <>
                        <h2 className={cx('section-title')}>Các tính năng chính</h2>
                        <ul className={cx('features-list')}>
                            {features.map((feature, index) => (
                                <li key={index}>{feature}</li>
                            ))}
                        </ul>
                    </>
                )}

                {tabId === 'technologies' && technologies && (
                    <>
                        <h2 className={cx('section-title')}>Công nghệ sử dụng</h2>

                        <div className={cx('tech-categories')}>
                            {technologies.frontend && technologies.frontend.length > 0 && (
                                <div className={cx('tech-category')}>
                                    <h3 className={cx('category-title')}>
                                        <CodeOutlined className={cx('category-icon')} />
                                        Frontend
                                    </h3>
                                    <div className={cx('tech-tags')}>
                                        {technologies.frontend.map((tech, index) => (
                                            <span key={index} className={cx('tech-tag')}>
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {technologies.backend && technologies.backend.length > 0 && (
                                <div className={cx('tech-category')}>
                                    <h3 className={cx('category-title')}>
                                        <CodeOutlined className={cx('category-icon')} />
                                        Backend
                                    </h3>
                                    <div className={cx('tech-tags')}>
                                        {technologies.backend.map((tech, index) => (
                                            <span key={index} className={cx('tech-tag')}>
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {technologies.database && technologies.database.length > 0 && (
                                <div className={cx('tech-category')}>
                                    <h3 className={cx('category-title')}>
                                        <DatabaseOutlined className={cx('category-icon')} />
                                        Cơ sở dữ liệu
                                    </h3>
                                    <div className={cx('tech-tags')}>
                                        {technologies.database.map((tech, index) => (
                                            <span key={index} className={cx('tech-tag')}>
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {technologies.cloud && technologies.cloud.length > 0 && (
                                <div className={cx('tech-category')}>
                                    <h3 className={cx('category-title')}>
                                        <CloudOutlined className={cx('category-icon')} />
                                        Cloud & DevOps
                                    </h3>
                                    <div className={cx('tech-tags')}>
                                        {technologies.cloud.map((tech, index) => (
                                            <span key={index} className={cx('tech-tag')}>
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {technologies.other && technologies.other.length > 0 && (
                                <div className={cx('tech-category')}>
                                    <h3 className={cx('category-title')}>
                                        <AppstoreOutlined className={cx('category-icon')} />
                                        Công nghệ khác
                                    </h3>
                                    <div className={cx('tech-tags')}>
                                        {technologies.other.map((tech, index) => (
                                            <span key={index} className={cx('tech-tag')}>
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className={cx('project-detail', { visible: isVisible })}>
            {/* Tabs navigation */}
            <div className={cx('detail-tabs')}>
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={cx('tab-button', { active: activeTab === tab.id })}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div className={cx('detail-content')}>
                {renderTabContent(activeTab)}

                {/* Hiển thị responsibilities nếu tab là description */}
                {activeTab === 'description' && responsibilities && responsibilities.length > 0 && (
                    <div className={cx('responsibilities-section')}>
                        <h3 className={cx('sub-title')}>Trách nhiệm của tôi</h3>
                        <ul className={cx('responsibilities-list')}>
                            {responsibilities.map((responsibility, index) => (
                                <li key={index}>{responsibility}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Hiển thị screenshots nếu tab là description */}
                {activeTab === 'description' && screenshots && screenshots.length > 0 && (
                    <div className={cx('screenshots-section')}>
                        <h3 className={cx('sub-title')}>Hình ảnh dự án</h3>
                        <div className={cx('screenshots-grid')}>
                            <Row gutter={[16, 16]}>
                                {screenshots.map((screenshot, index) => (
                                    <Col xs={24} sm={12} md={8} key={index}>
                                        <div className={cx('screenshot-item')}>
                                            <Image
                                                src={screenshot.url}
                                                alt={screenshot.caption}
                                                className={cx('screenshot-img')}
                                                preview={{
                                                    mask: (
                                                        <div className={cx('preview-mask')}>
                                                            <FullscreenOutlined />
                                                            <span>Xem</span>
                                                        </div>
                                                    ),
                                                }}
                                            />
                                            {screenshot.caption && (
                                                <div className={cx('screenshot-caption')}>{screenshot.caption}</div>
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProjectDetail;
